import React, { forwardRef } from 'react';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import PostCommunicationProvider, {
  PostCommunicationRef,
} from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { Icon, IconButton } from '@kontentino/ui';
import { faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightAndArrowDownLeftFromCenter';
import PostCommunicationTabBar from './postCommunication/PostCommunicationTabBar';
import { PostCommunicationContent } from './postCommunication/types/postCommunicationContent';
import ActivityLog from './postCommunication/ActivityLog';
import Comments from './postCommunication/Comments';
import PostCommunicationFullScreenModal from './postCommunication/PostCommunicationFullScreenModal';
import { stopPropagation } from 'utils/mouseEvent';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import useBoolean from 'utils/hooks/useBoolean';
import { useUser } from 'modules/user/userSelector';
import PostVersions from './postCommunication/PostRevisions';

export type PostCommunicationProps = {
  postId: number;
  enableRespondToApprovalRequest?: boolean;
  dataName?: string;
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
  emptyState?: React.ReactNode;
  initialActiveContent?: PostCommunicationContent;
};

const PostCommunication = forwardRef<
  PostCommunicationRef,
  PostCommunicationProps
>(
  (
    {
      dataName,
      postId,
      enableRespondToApprovalRequest,
      accordionProps,
      emptyState,
      initialActiveContent,
    },
    ref,
  ) => {
    const [areCommentsExpanded, setCommentsExpanded] = useBoolean(false);
    const user = useUser();

    return (
      <>
        <PostCommunicationProvider
          ref={ref}
          user={user}
          postId={postId}
          enableRespondToApprovalRequest={enableRespondToApprovalRequest}
          emptyState={emptyState}
          initialActiveContent={initialActiveContent}
        >
          {(context) => (
            <>
              <AccordionWidget
                icon={<AccordionWidgetIcon variant="communication" />}
                onOpenChange={(isOpen) =>
                  context.setReadyToTrackVisibility(isOpen)
                }
                titleSuffix={
                  <IconButton
                    icon={
                      <Icon icon={faArrowUpRightAndArrowDownLeftFromCenter} />
                    }
                    size="small"
                    variant="plain"
                    className="tw-text-grayscale-100"
                    data-name="communication_expand-comments"
                    onClick={stopPropagation(() => {
                      setCommentsExpanded.on();
                    })}
                  />
                }
                title={
                  <AccordionWidgetTitle
                    variant="communication"
                    className="tw-flex tw-justify-between"
                  />
                }
                data-name={dataName}
                {...accordionProps}
              >
                <>
                  <PostCommunicationTabBar
                    activeTab={context.activeContent}
                    onActiveTabChange={context.changeActiveContent}
                  />
                  {[
                    PostCommunicationContent.INTERNAL,
                    PostCommunicationContent.CLIENT,
                    PostCommunicationContent.PINNED,
                  ].includes(context.activeContent) && (
                    <Comments type={context.activeContent} />
                  )}
                  {context.activeContent ===
                    PostCommunicationContent.ACTIVITY && (
                    <ActivityLog
                      postId={context.postId}
                      className="tw-max-h-[25rem] tw-min-h-[15rem]"
                    />
                  )}
                  {context.activeContent ===
                    PostCommunicationContent.VERSIONS && (
                    <PostVersions postId={context.postId} />
                  )}
                </>
              </AccordionWidget>
            </>
          )}
        </PostCommunicationProvider>
        <UserPermissionGate
          exact={false}
          scopes={[
            UserPermission.POST_COMMUNICATION_CLIENT_SHOW,
            UserPermission.POST_COMMUNICATION_INTERNAL_SHOW,
          ]}
        >
          <PostCommunicationProvider
            user={user}
            postId={postId}
            enableRespondToApprovalRequest={enableRespondToApprovalRequest}
            emptyState={emptyState}
            initialActiveContent={initialActiveContent}
          >
            <PostCommunicationFullScreenModal
              isOpen={areCommentsExpanded}
              onClose={setCommentsExpanded.off}
            />
          </PostCommunicationProvider>
        </UserPermissionGate>
      </>
    );
  },
);

export default PostCommunication;
